/**
 * Tiny store that toggles the embedded Stripe payment modal. `startCheckout`
 * (useBloodCheckout) flips this open instead of redirecting to hosted Checkout;
 * a single <EmbeddedPaymentModal> mounted in the app reads it.
 */
import { create } from "zustand";

interface EmbeddedPaymentState {
  open: boolean;
  setOpen: (open: boolean) => void;
  // True from the moment payment is captured until the ConfirmationStep renders.
  // Keeps a full-screen "finalizing your booking" overlay up so the 2–3s while
  // the booking submit runs isn't a blank gap.
  finalizing: boolean;
  setFinalizing: (finalizing: boolean) => void;
  // Validation gate for the member-checkout CTAs. BookingForm holds the
  // react-hook-form instance, so it registers a validator here that runs
  // form.trigger() (surfacing errors + scroll/focus) and resolves true only when
  // the booking form is valid. The payment CTAs in PaymentInfo live outside the
  // form, so they call this before opening the embedded payment modal instead of
  // opening it unconditionally.
  validatePaymentGate: (() => Promise<boolean>) | null;
  setValidatePaymentGate: (fn: (() => Promise<boolean>) | null) => void;
  // Edge-function-INDEPENDENT last-resort capture. BookingForm registers a fn
  // that writes a recovery payload straight to booking_submission_attempts over
  // PostgREST. startCheckout calls it BEFORE opening the payment modal, so the
  // customer's data is durable before any TWINT/Klarna/PayPal redirect tears the
  // page down — and it survives every edge function being slow or down.
  captureBookingAttempt: (() => void) | null;
  setCaptureBookingAttempt: (fn: (() => void) | null) => void;
  // Durable-booking link: BookingForm registers a getter that assembles the full
  // submit-4 booking payload from the current form values (the same payload the
  // happy-path submit sends). EmbeddedPaymentModal calls it to forward the
  // payload to create-blood-checkout, which stashes it in pending_bookings so the
  // finalize-pending-booking webhook can replay the booking if the browser never
  // returns. Cleared on unmount.
  getBookingPayload: (() => Record<string, unknown> | null) | null;
  setGetBookingPayload: (
    fn: (() => Record<string, unknown> | null) | null,
  ) => void;
}

export const useEmbeddedPaymentStore = create<EmbeddedPaymentState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  finalizing: false,
  setFinalizing: (finalizing) => set({ finalizing }),
  validatePaymentGate: null,
  setValidatePaymentGate: (fn) => set({ validatePaymentGate: fn }),
  captureBookingAttempt: null,
  setCaptureBookingAttempt: (fn) => set({ captureBookingAttempt: fn }),
  getBookingPayload: null,
  setGetBookingPayload: (fn) => set({ getBookingPayload: fn }),
}));
